// =============================================================
// brand.jsx — an outfitter's logo and colours on the pages a GUEST sees (the
// trip portal, the public booking page). SELF-CONTAINED / REMOVABLE.
//
// The server decides everything (api/src/outfitterBrand.js → publicBrand): the
// logo URL, the primary/secondary colours and the readable text colour on each.
// Pages wrap themselves in <BrandProvider brand={…}>; the Lodge UI primitives
// that carry the accent colour (Btn, RadioCards, SegmentedChoice, link
// buttons) read useBrand() and fall back to the Harvus look when there is none.
// The Lodge console sets one too — the selected outfitter's colours
// (shared/consoleTheme.js) — with `console` keeping secondary buttons neutral.
// =============================================================

import React, { createContext, useContext } from 'react';
import { B, space, font, weight } from '../../../constants/brand';

const BrandContext = createContext(null);
const HEX = /^#[0-9a-f]{6}$/i;

// Only well-formed values pass — a colour lands in inline styles.
export function sanitizeBrand(raw) {
  // An outfitter that set nothing keeps the Harvus look rather than a band of
  // the default colour.
  if (!raw || typeof raw !== 'object' || raw.isDefault) return null;
  const hex = (v) => (HEX.test(String(v || '')) ? String(v) : null);
  const logo = typeof raw.logoUrl === 'string' && (/^https:\/\//i.test(raw.logoUrl) || raw.logoUrl.startsWith('/api/')) ? raw.logoUrl : null;
  const primary = hex(raw.primaryColor);
  if (!primary && !logo) return null;
  return {
    name: raw.name || null,
    logoUrl: logo,
    primary: primary || B.navy,
    onPrimary: hex(raw.onPrimary) || '#ffffff',
    secondary: hex(raw.secondaryColor) || primary || B.navy,
    onSecondary: hex(raw.onSecondary) || '#ffffff',
    // The Lodge console (shared/consoleTheme.js) themes from the selected
    // outfitter; there a secondary button stays the neutral outline.
    console: raw.console === true,
  };
}

export function BrandProvider({ brand, children }) {
  return <BrandContext.Provider value={sanitizeBrand(brand)}>{children}</BrandContext.Provider>;
}

export const useBrand = () => useContext(BrandContext);
// The accent colour a primitive should use: the lodge's, else Harvus navy.
export const useAccent = () => { const b = useContext(BrandContext); return b ? b.primary : B.navy; };

// The lodge's name and logo across the top of a guest page, on a band of its
// primary colour when there is no logo (the same rule as the email header).
export function BrandHeader({ fallbackName }) {
  const b = useBrand();
  const name = (b && b.name) || fallbackName;
  if (!b) {
    return name ? <div style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.textMd, marginBottom: space.md }}>{name}</div> : null;
  }
  if (b.logoUrl) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, paddingBottom: space.md, marginBottom: space.lg, borderBottom: `3px solid ${b.secondary}` }}>
        <img src={b.logoUrl} alt={name || 'Logo'} style={{ display: 'block', height: 56, maxWidth: 260, width: 'auto', objectFit: 'contain' }} />
      </div>
    );
  }
  return (
    <div style={{ background: b.primary, color: b.onPrimary, borderRadius: 12, padding: `${space.md}px ${space.lg}px`, marginBottom: space.lg, fontSize: font.lg, fontWeight: weight.bold, letterSpacing: 0.1 }}>
      {name}
    </div>
  );
}
